import ClearIcon from "@mui/icons-material/Clear";
import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  Container,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import ExpandableTable from "components/ExpandableTable";
import {
  DEFAULT_ROWS_PER_PAGE,
  TableCellType,
  TableSortType,
} from "components/LazyTable";
import RequestResponseBox from "components/RequestResponseBox";
import SelectEntity from "components/SelectEntity";
import SnackbarMessage from "components/SnackbarMessage";
import { UserSearchSelect } from "components/UserSearchSelect";
import { isValid } from "date-fns";
import useGlobalContext from "hooks/globalData";
import useDebounce from "hooks/useDebounce";
import useListTitle from "hooks/useListTitle";
import { useStrictQuery } from "hooks/useStrictQuery";
import useTrashablePromises from "hooks/useTrashablePromises";
import { useEffect, useState } from "react";
import { FormattedMessage as T, useIntl } from "react-intl";
import { useQuery, useQueryClient } from "react-query";
import {
  ListRequestOfBaseFilter,
  ListRequestOfRequestReprocess,
  ListRequestOfRequestFilter,
  ListResponseOfRequest,
  ListResponseOfRequestStatus,
  ListResponseOfRequestType,
  RequestFilter,
} from "services/KnowfullyAdminClient";
import contextStore from "stores/contextStore";
import persistanceStore from "stores/persistanceStore";
import { Severity, SnackbarMessageProps } from "types/common-types";
import { standardDateFormat } from "utils/dateUtils";
import translateError from "utils/translateError";

const RequestsList = () => {
  const queryClient = useQueryClient();
  const { registerPromise } = useTrashablePromises();
  const [order, setOrder] = useState<TableSortType>("desc");
  const [orderBy, setOrderBy] = useState("CreatedOn");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const { formatMessage: t } = useIntl();
  const { requestService } = useGlobalContext();
  const { organizationId } = contextStore.useState();
  const searchTerm = persistanceStore.useState((s) => s.requestSearchTerm);
  const trimmedSearchTerm = searchTerm.trim();
  const debouncedSearchTerm = useDebounce(trimmedSearchTerm, 500);
  const requestTypeId = persistanceStore.useState((s) => s.requestTypeId);
  const requestStatusId = persistanceStore.useState((s) => s.requestStatusId);
  const requestStartDateSearch = persistanceStore.useState(
    (s) => s.requestStartDateSearch
  );
  const requestEndDateSearch = persistanceStore.useState(
    (s) => s.requestEndDateSearch
  );
  const [
    snackbarMessage,
    setSnackbarMessage,
  ] = useState<SnackbarMessageProps | null>(null);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);

  useListTitle("requestsLabel");

  const requestTypesResponse = useQuery<ListResponseOfRequestType>(
    "requestTypes",
    () =>
      requestService.getRequestTypes(
        new ListRequestOfBaseFilter({
          sortField: "name",
          sortDescending: true,
        })
      )
  );

  const requestStatusesResponse = useQuery<ListResponseOfRequestStatus>(
    "requestStatuses",
    () =>
      requestService.getRequestStatuses(
        new ListRequestOfBaseFilter({
          sortField: "name",
          sortDescending: true,
        })
      )
  );

  const {
    data,
    isFetching,
    refetch,
  } = useStrictQuery<ListResponseOfRequest>(
    [
      "requests",
      order,
      orderBy,
      page,
      rowsPerPage,
      organizationId,
      selectedUserId,
    ],
    () =>
      requestService.getRequests(
        ListRequestOfRequestFilter.fromJS({
          sortField: orderBy,
          sortDescending: order === "desc",
          pageSize: rowsPerPage,
          pageNumber: page,
          parameters: RequestFilter.fromJS({
            searchText: debouncedSearchTerm,
            dateFrom: requestStartDateSearch,
            dateTo: requestEndDateSearch,
            statusId: requestStatusId,
            typeId: requestTypeId,
            userId: selectedUserId,
          }),
        })
      ),
    {
      keepPreviousData: true,
    }
  );

  useEffect(() => {
    if (debouncedSearchTerm && !selectedUserId) {
      if (
        requestStartDateSearch === undefined ||
        requestEndDateSearch === undefined
      ) {
        setSnackbarMessage(
          new SnackbarMessageProps(
            t({ id: "requestsValidationSearch" }),
            Severity.Warning
          )
        );
        return;
      }

      let difference = Math.floor(
        (Date.UTC(
          requestEndDateSearch.getFullYear(),
          requestEndDateSearch.getMonth(),
          requestEndDateSearch.getDate()
        ) -
          Date.UTC(
            requestStartDateSearch.getFullYear(),
            requestStartDateSearch.getMonth(),
            requestStartDateSearch.getDate()
          )) /
          (1000 * 60 * 60 * 24)
      );

      if (difference > 7) {
        setSnackbarMessage(
          new SnackbarMessageProps(
            t({ id: "requestsValidationSearch" }),
            Severity.Warning
          )
        );
        return;
      }
    }

    setPage(0);
    refetch();
  }, [
    refetch,
    debouncedSearchTerm,
    requestStatusId,
    requestTypeId,
    requestStartDateSearch,
    requestEndDateSearch,
    t,
    selectedUserId,
  ]);

  const rawSummary = (data?.data ?? []).map((row) => ({
    ...row,
    id: row.id!,
    isExpandable: true,
    renderExpandedData: () => (
      <RequestResponseBox
        requestObject={row.requestObject}
        responseObject={row.responseObject}
      />
    ),
  }));

  const onUpdateStartDate = (date: Date) => {
    persistanceStore.update((s) => {
      if (isValid(date)) {
        s.requestStartDateSearch = date;
      } else if (date == null) {
        s.requestStartDateSearch = undefined;
      }
    });
  };

  const onUpdateEndDate = (date: Date) => {
    persistanceStore.update((s) => {
      if (isValid(date)) {
        s.requestEndDateSearch = date;
      } else if (date == null) {
        s.requestEndDateSearch = undefined;
      }
    });
  };

  const executeReprocess = async (ids: string[]) => {
    try {
      const requestReprocess = ListRequestOfRequestReprocess.fromJS({
        parameters: {
          requestIds: ids,
        },
      });

      await registerPromise(requestService.requestReprocess(requestReprocess));
      queryClient.invalidateQueries("requests");

      setSnackbarMessage(
        new SnackbarMessageProps(t({ id: "reprocessFinished" }))
      );
    } catch (err) {
      console.log(err);
      setSnackbarMessage(
        new SnackbarMessageProps(translateError(err, t), Severity.Error)
      );
    }
  };

  const cells: Array<TableCellType> = [
    {
      accessor: "partnerName",
      align: "left",
      width: 0,
      type: "text",
      label: t({ id: "partnerLabel" }),
      noSort: true,
    },
    {
      accessor: "createdOn",
      align: "left",
      width: 0,
      type: "datetime",
      label: t({ id: "dateTimeLabel" }),
      noSort: true,
    },
    {
      accessor: "type",
      align: "left",
      width: 0,
      type: "text",
      label: t({ id: "typeLabel" }),
      noSort: true,
    },
    {
      accessor: "status",
      align: "left",
      width: 0,
      type: "text",
      label: t({ id: "statusLabel" }),
      noSort: true,
    },
  ];

  return (
    <Container>
      <Box mb={3}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={12} md={12}>
            <UserSearchSelect
              value={selectedUserId}
              onChange={setSelectedUserId}
              organizationId={organizationId}
              label={t({ id: "userLabel" })}
            />
          </Grid>
          <Grid item xs={12} sm={12} md={6}>
            <TextField
              fullWidth
              variant="outlined"
              value={searchTerm ?? ""}
              placeholder={t({ id: "searchPlaceholder" })}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),

                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() =>
                        persistanceStore.update((s) => {
                          s.requestSearchTerm = "";
                        })
                      }
                    >
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              onChange={(e) =>
                persistanceStore.update((s) => {
                  s.requestSearchTerm = e.target.value;
                })
              }
            />
          </Grid>
          <Grid item xs={12} sm={12} md={3}>
            <DatePicker
              label={t({ id: "startDateLabel" })}
              disabled={isFetching}
              value={requestStartDateSearch || null}
              format={standardDateFormat}
              onChange={(date: any) => onUpdateStartDate(date)}
              slotProps={{
                textField: { fullWidth: true, variant: "standard" },
                actionBar: {
                  actions: ["clear", "cancel"],
                },
              }}
            />
          </Grid>
          <Grid item xs={12} sm={12} md={3}>
            <DatePicker
              label={t({ id: "endDateLabel" })}
              disabled={isFetching}
              value={requestEndDateSearch || null}
              format={standardDateFormat}
              onChange={(date: any) => onUpdateEndDate(date)}
              slotProps={{
                textField: { fullWidth: true, variant: "standard" },
                actionBar: {
                  actions: ["clear", "cancel"],
                },
              }}
            />
          </Grid>

          <Grid item xs={12} sm={12} md={6}>
            <SelectEntity
              value={requestTypeId || ""}
              fetchedData={requestTypesResponse}
              labelField="name"
              label={t({ id: "typeLabel" })}
              onChange={(id) =>
                persistanceStore.update((s) => {
                  s.requestTypeId = id;
                })
              }
              textFieldProps={{
                variant: "outlined",
              }}
            />
          </Grid>

          <Grid item xs={12} sm={12} md={6}>
            <SelectEntity
              value={requestStatusId || ""}
              fetchedData={requestStatusesResponse}
              labelField="name"
              label={t({ id: "statusLabel" })}
              onChange={(id) =>
                persistanceStore.update((s) => {
                  s.requestStatusId = id;
                })
              }
              textFieldProps={{
                variant: "outlined",
              }}
            />
          </Grid>
        </Grid>
      </Box>

      <ExpandableTable
        {...{
          rowCount: data?.totalRecords ?? 0,
          rows: rawSummary,
          loading: isFetching,
          dense: true,
          cells,
          page,
          setPage,
          order,
          setOrder,
          orderBy,
          setOrderBy,
          rowsPerPage,
          setRowsPerPage,
          executeMultiple: {
            onExecuteMultiple: async (ids: string[]) => {
              await executeReprocess(ids);
            },
            dialogLabel: t({ id: "reprocessRequestMessage" }),
            buttonLabel: t({ id: "reprocessButton" }),
          },
          titleComponent: (
            <Typography variant="h6">
              <T id="requestsLabel" />
            </Typography>
          ),
          collapsable: true,
        }}
      />

      <SnackbarMessage
        snackbarMessage={snackbarMessage}
        setSnackbarMessage={setSnackbarMessage}
      />
    </Container>
  );
};

export default RequestsList;
